import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  AddCircle as AddCircleIcon,
  Warning as WarningIcon,
  VpnKey as VpnKeyIcon,
  AttachMoney as AttachMoneyIcon,
  Notes as NotesIcon,
  Tune as TuneIcon,
  CheckCircle as CheckCircleIcon,
  Toll as TollIcon,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import { addNovelty, getPeajes } from "modules/finops/conciliaciones/services/financieraService";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  success: "#27ae60",
  error: "#d32f2f",
  info: "#2980b9",
  warning: "#ed6c02",
  red: "#c0392b",
  redLight: "#fdecea",
  redDark: "#a93226",
  gray: "#f8fafc",
  border: "#e2e8f0",
};

const MIN_OBSERVATION = 10;

// ================================================
// HELPERS DE FORMATEO / PARSEO
// ================================================
const formatNumberCO = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";

  const str = String(raw).trim();
  const parts = str.split(".");
  let intPart = parts[0].replace(/\D/g, "");
  const decPart = parts[1] ? parts[1].replace(/\D/g, "") : null;

  if (intPart === "" && decPart === null) return "";

  const formattedInt = intPart === "" ? "" : Number(intPart).toLocaleString("es-CO");

  if (decPart !== null) {
    return `${formattedInt},${decPart}`;
  }

  return formattedInt;
};

const ModalAgregarNovedad = ({
  open,
  onClose,
  onSuccess,
  onError,
  keyConcil,
  code,
  conciliationName,
}) => {
  const [typeNov, setTypeNov] = useState(1);
  const [valueDisplay, setValueDisplay] = useState("");
  const [valueNumber, setValueNumber] = useState(null);
  const [observation, setObservation] = useState("");
  const [peaje, setPeaje] = useState("");
  const [peajesOptions, setPeajesOptions] = useState([]);
  const [loadingPeajes, setLoadingPeajes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setTypeNov(1);
      setValueDisplay("");
      setValueNumber(null);
      setObservation("");
      setPeaje("");
      setFieldErrors({});
      setSubmitting(false);
    }
  }, [open]);

  // 🆕 Cargar peajes cuando se abre el modal y hay `code`
  useEffect(() => {
    if (!open || !code) {
      setPeajesOptions([]);
      return;
    }

    let active = true;
    setLoadingPeajes(true);

    getPeajes(code)
      .then((result) => {
        if (!active) return;
        if (result.success) {
          setPeajesOptions(result.data || []);
        } else {
          setPeajesOptions([]);
        }
      })
      .catch((err) => {
        console.error("Error cargando peajes:", err);
        if (active) setPeajesOptions([]);
      })
      .finally(() => {
        if (active) setLoadingPeajes(false);
      });

    return () => {
      active = false;
    };
  }, [open, code]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleValueChange = (e) => {
    const raw = e.target.value;

    if (raw !== "" && !/^[\d.,]*$/.test(raw)) return;

    const cleaned = String(raw).replace(/\./g, "").replace(",", ".");
    const numeric = parseFloat(cleaned);

    if (raw === "") {
      setValueDisplay("");
      setValueNumber(null);
    } else if (!Number.isNaN(numeric)) {
      setValueDisplay(formatNumberCO(cleaned));
      setValueNumber(numeric);
    } else {
      setValueDisplay(raw);
      setValueNumber(null);
    }

    if (fieldErrors.value) {
      setFieldErrors((prev) => ({ ...prev, value: null }));
    }
  };

  const handleObservationChange = (e) => {
    const raw = e.target.value;
    setObservation(raw);
    if (fieldErrors.observation && raw.trim().length >= MIN_OBSERVATION) {
      setFieldErrors((prev) => ({ ...prev, observation: null }));
    }
  };

  const validate = useCallback(() => {
    const errors = {};

    if (!keyConcil) errors.key_concil = "La llave es requerida";
    if (!code && code !== 0) errors.code = "El código es requerido";

    if (typeNov !== 1 && typeNov !== 2) {
      errors.type_nov = "Seleccione un tipo válido";
    }

    if (valueNumber === null || valueNumber === undefined || Number.isNaN(valueNumber)) {
      errors.value = "El valor es requerido";
    } else if (valueNumber <= 0) {
      errors.value = "El valor debe ser mayor a 0";
    }

    const obs = String(observation || "").trim();
    if (!obs) {
      errors.observation = "La observación es requerida";
    } else if (obs.length < MIN_OBSERVATION) {
      errors.observation = `Mínimo ${MIN_OBSERVATION} caracteres (${obs.length}/${MIN_OBSERVATION})`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [keyConcil, code, typeNov, valueNumber, observation]);

  const handleSubmit = async () => {
    if (!validate()) {
      setSnackbar({
        open: true,
        message: "Revise los campos marcados en rojo",
        severity: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await addNovelty({
        code,
        key_concil: keyConcil,
        type_nov: typeNov,
        value: valueNumber,
        observation: observation.trim(),
        peaje: peaje || null,
      });

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || "Novedad creada exitosamente",
          severity: "success",
        });

        if (onSuccess) onSuccess(result);

        setTimeout(() => {
          onClose && onClose();
        }, 900);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Error al crear la novedad",
          severity: "error",
        });
        if (onError) onError(result.message);
      }
    } catch (error) {
      console.error("❌ Error creando novedad:", error);
      const msg = error.message || "Error al crear la novedad";
      setSnackbar({ open: true, message: msg, severity: "error" });
      if (onError) onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const obsLen = String(observation || "").trim().length;
  const obsValid = obsLen >= MIN_OBSERVATION;

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.red} 0%, ${colors.redDark} 100%)`,
            color: "#fff",
            py: 2.5,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <AddCircleIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color="white">
                Agregar Novedad
              </MDTypography>
              {conciliationName && (
                <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.85)" }}>
                  {conciliationName}
                </MDTypography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="large" sx={{ color: "#fff" }} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Campos fijos (solo lectura) */}
          <Box
            sx={{
              p: 2,
              mt: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: colors.redLight,
              border: `1px solid ${colors.red}30`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <WarningIcon sx={{ color: colors.red, fontSize: 20 }} />
              <MDTypography variant="subtitle2" fontWeight="bold" color={colors.redDark}>
                Información de la conciliación
              </MDTypography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Llave de Conciliación"
                  value={keyConcil || ""}
                  size="small"
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon sx={{ fontSize: 18, color: colors.info }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "#fff",
                      fontFamily: "monospace",
                      fontWeight: 600,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Código (Concesión)"
                  value={code || ""}
                  size="small"
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon sx={{ fontSize: 18, color: colors.success }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "#fff",
                      fontFamily: "monospace",
                      fontWeight: 600,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            {/* 🆕 Select de Peaje — PRIMERO */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Peaje (opcional)"
                value={peaje}
                onChange={(e) => setPeaje(e.target.value)}
                disabled={submitting || loadingPeajes}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TollIcon sx={{ fontSize: 18, color: colors.info }} />
                    </InputAdornment>
                  ),
                  endAdornment: loadingPeajes ? (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  ) : null,
                }}
                helperText={
                  loadingPeajes
                    ? "Cargando peajes..."
                    : peajesOptions.length === 0
                    ? "No hay peajes disponibles para esta concesión"
                    : "Seleccione un peaje o déjelo vacío"
                }
                sx={{
                  "& .MuiInputBase-root": { height: "48px", borderRadius: 2 },
                }}
              >
                <MenuItem value="">
                  <MDTypography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                    — Sin peaje —
                  </MDTypography>
                </MenuItem>

                {peajesOptions.map((p) => (
                  <MenuItem key={p.peaje} value={p.peaje}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TollIcon sx={{ fontSize: 16, color: colors.info }} />
                      <MDTypography variant="body2">{p.peaje}</MDTypography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Tipo de Novedad */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tipo de Novedad *"
                value={typeNov}
                onChange={(e) => setTypeNov(Number(e.target.value))}
                disabled={submitting}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TuneIcon sx={{ fontSize: 18, color: colors.info }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": { height: "48px", borderRadius: 2 },
                }}
              >
                <MenuItem value={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label="Abono"
                      size="small"
                      sx={{
                        bgcolor: `${colors.success}20`,
                        color: colors.success,
                        fontWeight: 700,
                        fontSize: "0.7rem",
                      }}
                    />
                    <MDTypography variant="body2">Tipo 1 — Abono</MDTypography>
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label="Ajuste"
                      size="small"
                      sx={{
                        bgcolor: `${colors.warning}20`,
                        color: colors.warning,
                        fontWeight: 700,
                        fontSize: "0.7rem",
                      }}
                    />
                    <MDTypography variant="body2">Tipo 2 — Ajuste</MDTypography>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>

            {/* Valor */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor *"
                value={valueDisplay}
                onChange={handleValueChange}
                disabled={submitting}
                size="small"
                placeholder="Ej: 200.000 o 200000"
                error={!!fieldErrors.value}
                helperText={
                  fieldErrors.value ||
                  "Escriba solo números. Se formatean con puntos de miles automáticamente"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MDTypography variant="body2" fontWeight="bold" sx={{ color: colors.red }}>
                        $
                      </MDTypography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": { height: "48px", borderRadius: 2 },
                  "& input": { fontFamily: "monospace", fontWeight: 600 },
                }}
              />
            </Grid>

            {/* Observación */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observación *"
                value={observation}
                onChange={handleObservationChange}
                disabled={submitting}
                multiline
                minRows={3}
                maxRows={5}
                placeholder="Escriba el motivo de la novedad (mínimo 10 caracteres)"
                error={!!fieldErrors.observation}
                helperText={
                  fieldErrors.observation ||
                  `Mínimo ${MIN_OBSERVATION} caracteres (${obsLen}/${MIN_OBSERVATION})`
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                      <NotesIcon sx={{ fontSize: 18, color: colors.info }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": { borderRadius: 2 },
                }}
              />
              {!obsValid && obsLen > 0 && (
                <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                  <WarningIcon sx={{ fontSize: 14, color: colors.warning }} />
                  <MDTypography variant="caption" color={colors.warning}>
                    Faltan {MIN_OBSERVATION - obsLen} caracteres
                  </MDTypography>
                </Box>
              )}
              {obsValid && (
                <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: colors.success }} />
                  <MDTypography variant="caption" color={colors.success}>
                    Observación válida
                  </MDTypography>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Resumen */}
          {typeNov && valueNumber !== null && obsValid && (
            <Alert
              severity="info"
              sx={{
                mt: 3,
                bgcolor: `${colors.info}10`,
                "& .MuiAlert-icon": { color: colors.info },
              }}
            >
              <MDTypography variant="body2" fontWeight="bold" color={colors.info}>
                Resumen
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                Se registrará una novedad de tipo{" "}
                <strong>{typeNov === 1 ? "Abono" : "Ajuste"}</strong> con valor{" "}
                <strong>
                  $
                  {Number(valueNumber || 0).toLocaleString("es-CO", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                </strong>{" "}
                {peaje && (
                  <>
                    para el peaje <strong>{peaje}</strong>{" "}
                  </>
                )}
                en la concesión <strong>{code}</strong>.
              </MDTypography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            bgcolor: colors.gray,
            borderTop: `1px solid ${colors.border}`,
            gap: 1,
          }}
        >
          <MDButton
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              textTransform: "none",
              color: "#64748b",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", backgroundColor: "transparent" },
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={
              submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <AddCircleIcon />
            }
            sx={{
              bgcolor: colors.red,
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              "&:hover": { bgcolor: colors.redDark },
              "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            {submitting ? "Guardando..." : "Guardar Novedad"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{
            width: "100%",
            minWidth: "300px",
            backgroundColor: snackbar.severity === "success" ? "#f0fff4" : "#fff5f5",
            color: snackbar.severity === "success" ? "#276749" : "#c53030",
            "& .MuiAlert-icon": {
              color: snackbar.severity === "success" ? "#276749" : "#c53030",
            },
            border: snackbar.severity === "success" ? "1px solid #c6f6d5" : "1px solid #fed7d7",
          }}
        >
          <MDTypography variant="body1" fontWeight="bold">
            {snackbar.severity === "success" ? "¡Éxito!" : "Error"}
          </MDTypography>
          <MDTypography variant="body2">{snackbar.message}</MDTypography>
        </Alert>
      </Snackbar>
    </>
  );
};

ModalAgregarNovedad.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  keyConcil: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  conciliationName: PropTypes.string,
};

ModalAgregarNovedad.defaultProps = {
  onSuccess: null,
  onError: null,
  keyConcil: null,
  code: null,
  conciliationName: null,
};

export default ModalAgregarNovedad;
